import { useState, useMemo, useCallback, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  reportConfig,
  flowStateConfig,
  resolutionConfig,
  saDashboardStats,
  reports,
  reincidencias,
  reportRules,
  getReportsByType,
  getReportsByFlowState,
  getReportsPending,
  getReportsResolved,
  getReincidenciasByStatus,
  type ReportType,
  type ReportFlowState,
  type ReportResolution,
  type ReportEntry,
  type ReincidenciaEntry,
} from '@/mocks/rfwReports';

type PageState = 'loading' | 'error' | 'ready';
type ViewSection = 'reportes' | 'flujo' | 'reincidencias' | 'sa-dashboard';

function TypeBadge({ type }: { type: ReportType }) {
  const c = reportConfig[type];
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-semibold ${c.bgColor} ${c.color} border border-current/20`}>
      <i className={`${c.icon} text-[8px]`}></i>
      {c.label}
    </span>
  );
}

function FlowBadge({ state }: { state: ReportFlowState }) {
  const c = flowStateConfig[state];
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-semibold ${c.bgColor} ${c.color} border border-current/20`}>
      <i className={`${c.icon} text-[8px]`}></i>
      {c.label}
    </span>
  );
}

function ResolutionBadge({ resolution }: { resolution: ReportResolution }) {
  const c = resolutionConfig[resolution];
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-semibold ${c.bgColor} ${c.color} border border-current/20`}>
      <i className={`${c.icon} text-[8px]`}></i>
      {c.label}
    </span>
  );
}

export default function ReportesPage() {
  const [pageState, setPageState] = useState<PageState>('loading');
  const [activeView, setActiveView] = useState<ViewSection>('reportes');
  const [typeFilter, setTypeFilter] = useState<ReportType | 'todas'>('todas');
  const [flowFilter, setFlowFilter] = useState<ReportFlowState | 'todas'>('todas');
  const [reincFilter, setReincFilter] = useState<ReincidenciaEntry['status'] | 'todas'>('todas');
  const [selectedReport, setSelectedReport] = useState<string | null>(null);
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportForm, setReportForm] = useState({ type: 'spam' as ReportType, target: '', reason: '' });

  useEffect(() => {
    const t = setTimeout(() => setPageState('ready'), 400);
    return () => clearTimeout(t);
  }, []);

  const handleRetry = useCallback(() => {
    setPageState('loading');
    setTimeout(() => setPageState('ready'), 400);
  }, []);

  const handleSubmitReport = () => {
    setShowReportModal(false);
    setReportForm({ type: 'spam', target: '', reason: '' });
  };

  const filtered = useMemo(() => {
    let result = typeFilter === 'todas' ? reports : getReportsByType(typeFilter);
    if (flowFilter !== 'todas') result = result.filter((r) => r.flowState === flowFilter);
    return result;
  }, [typeFilter, flowFilter]);

  const pending = getReportsPending();
  const resolved = getReportsResolved();
  const filteredReinc = getReincidenciasByStatus(reincFilter);

  const views: { id: ViewSection; label: string; icon: string; badge?: number }[] = [
    { id: 'reportes', label: 'Reportes', icon: 'ri-flag-line', badge: reports.length },
    { id: 'flujo', label: 'Flujo de Moderación', icon: 'ri-git-branch-line', badge: 4 },
    { id: 'reincidencias', label: 'Reincidencias', icon: 'ri-loop-left-line', badge: reincidencias.length },
    { id: 'sa-dashboard', label: 'Dashboard SA', icon: 'ri-dashboard-line', badge: saDashboardStats.reportsPending },
  ];

  if (pageState === 'loading') {
    return (
      <div className="min-h-screen bg-background-50">
        <section className="relative w-full h-[200px] md:h-[260px] bg-background-200 animate-pulse" />
        <main className="max-w-[1280px] mx-auto px-4 md:px-6 py-8 space-y-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="md:col-span-1 bg-background-100 rounded-2xl h-[600px] animate-pulse" />
            <div className="md:col-span-2 bg-background-100 rounded-2xl h-[600px] animate-pulse" />
          </div>
        </main>
      </div>
    );
  }

  if (pageState === 'error') {
    return (
      <div className="min-h-screen bg-background-50 flex flex-col items-center justify-center px-4 py-20">
        <div className="w-20 h-20 rounded-3xl bg-red-100 flex items-center justify-center mb-5">
          <i className="ri-error-warning-line text-3xl text-red-500"></i>
        </div>
        <h2 className="text-xl font-extrabold text-foreground-950 mb-2">Error al cargar reportes</h2>
        <p className="text-sm text-foreground-500 mb-6 text-center max-w-md">No pudimos cargar los datos del sistema de reportes. Verificá tu conexión e intentá de nuevo.</p>
        <button onClick={handleRetry} className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-50 rounded-xl text-sm font-bold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap">
          <i className="ri-refresh-line"></i>Reintentar
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-50">
      {/* HERO */}
      <section className="relative w-full h-[220px] md:h-[320px] overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=Professional%20community%20reporting%20dashboard%20concept%20abstract%20geometric%20flag%20alert%20patterns%20floating%20translucent%20organized%20warm%20amber%20orange%20tones%20editorial%20product%20photography%20clean%20modern%20aesthetic%20soft%20shadows&width=1600&height=900&seq=rfw-reportes-hero&orientation=landscape"
          alt="Sistema de Reportes RFW"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/70"></div>
        <div className="absolute inset-0 flex flex-col items-center justify-center px-4 text-center">
          <div className="w-16 h-16 rounded-2xl bg-background-50/15 backdrop-blur-md flex items-center justify-center mb-4 border border-background-50/20">
            <i className="ri-flag-line text-3xl text-background-50"></i>
          </div>
          <h1 className="text-3xl md:text-5xl font-extrabold text-background-50 leading-tight mb-3">
            Sistema de Reportes
          </h1>
          <p className="text-sm md:text-base text-background-50/80 max-w-xl leading-relaxed">
            La comunidad se autorregula. Cinco tipos de reporte, un flujo de cuatro pasos, revisión humana obligatoria. Nada se elimina automáticamente.
          </p>
        </div>
      </section>

      {/* MAIN */}
      <main className="max-w-[1280px] mx-auto px-4 md:px-6 py-6 md:py-8">
        {/* LIVE PULSE BAR */}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3 mb-6">
          {[
            { label: 'Reportes Totales', value: (saDashboardStats.totalReports / 1000).toFixed(0) + 'K', icon: 'ri-flag-line', color: 'text-amber-600 bg-amber-50' },
            { label: 'Pendientes', value: saDashboardStats.reportsPending.toString(), icon: 'ri-hourglass-line', color: 'text-orange-600 bg-orange-50' },
            { label: 'En Revisión', value: saDashboardStats.reportsInReview.toString(), icon: 'ri-search-eye-line', color: 'text-sky-600 bg-sky-50' },
            { label: 'Resueltos', value: (saDashboardStats.reportsResolved / 1000).toFixed(0) + 'K', icon: 'ri-check-double-line', color: 'text-emerald-600 bg-emerald-50' },
            { label: 'Reincidencia', value: saDashboardStats.repeatOffenderRate, icon: 'ri-loop-left-line', color: 'text-red-600 bg-red-50' },
            { label: 'T. Resolución', value: saDashboardStats.avgResolutionTime, icon: 'ri-timer-line', color: 'text-slate-600 bg-slate-50' },
          ].map((stat, i) => (
            <div key={i} className="flex items-center gap-2.5 p-3 rounded-xl bg-background-50 border border-background-200/70">
              <div className={`w-8 h-8 rounded-full ${stat.color} flex items-center justify-center shrink-0`}>
                <i className={`${stat.icon} text-xs`}></i>
              </div>
              <div>
                <p className="text-sm font-extrabold text-foreground-950">{stat.value}</p>
                <p className="text-[9px] text-foreground-500">{stat.label}</p>
              </div>
            </div>
          ))}
        </div>

        {/* BUTTON: NUEVO REPORTE */}
        <div className="flex justify-end mb-4">
          <button
            onClick={() => setShowReportModal(true)}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-amber-500 hover:bg-amber-600 text-white font-semibold rounded-full transition-all duration-200 text-sm whitespace-nowrap cursor-pointer"
          >
            <i className="ri-flag-line"></i>
            Nuevo Reporte
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-6">
          {/* SIDEBAR */}
          <aside className="md:col-span-3">
            <div className="rounded-2xl bg-background-50 border border-background-200/70 overflow-hidden sticky top-4">
              <div className="p-4 border-b border-background-200/50">
                <h2 className="text-sm font-extrabold text-foreground-950 mb-1">Sistema de Reportes</h2>
                <p className="text-[10px] text-foreground-500">Autorregulación comunitaria</p>
              </div>

              <nav className="p-2">
                {views.map((v) => (
                  <button
                    key={v.id}
                    onClick={() => setActiveView(v.id)}
                    className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-left transition-all duration-200 cursor-pointer mb-1 ${
                      activeView === v.id
                        ? 'bg-amber-50 text-amber-700 font-bold'
                        : 'text-foreground-600 hover:bg-background-100'
                    }`}
                  >
                    <i className={`${v.icon} text-sm ${activeView === v.id ? 'text-amber-600' : ''}`}></i>
                    <span className="text-xs flex-1">{v.label}</span>
                    {v.badge !== undefined && v.badge > 0 && (
                      <span className={`w-5 h-5 rounded-full text-[9px] font-bold flex items-center justify-center ${
                        activeView === v.id ? 'bg-amber-200 text-amber-700' : 'bg-background-100 text-foreground-500'
                      }`}>
                        {v.badge}
                      </span>
                    )}
                  </button>
                ))}
              </nav>

              <div className="p-3 mx-2 mb-2 rounded-xl bg-background-100">
                <p className="text-[10px] font-bold text-foreground-600 mb-2">Tipos de Reporte</p>
                <div className="space-y-1">
                  {Object.entries(reportConfig).map(([key, c]) => (
                    <div key={key} className="flex items-center gap-2 text-[10px]">
                      <div className={`w-5 h-5 rounded-full ${c.bgColor} flex items-center justify-center shrink-0`}>
                        <i className={`${c.icon} text-[9px] ${c.color}`}></i>
                      </div>
                      <span className="text-foreground-600">{c.labelPlural}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </aside>

          {/* MAIN CONTENT */}
          <div className="md:col-span-9">
            {/* VIEW: REPORTES */}
            {activeView === 'reportes' && (
              <div className="space-y-5">
                <div className="flex flex-wrap items-center gap-1.5">
                  {([
                    { id: 'todas' as const, label: 'Todos los Tipos', icon: 'ri-list-check-3' },
                    ...Object.entries(reportConfig).map(([key, c]) => ({ id: key as ReportType, label: c.labelPlural, icon: c.icon })),
                  ]).map((f) => (
                    <button
                      key={f.id}
                      onClick={() => setTypeFilter(f.id)}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-medium transition-all duration-200 whitespace-nowrap cursor-pointer border ${
                        typeFilter === f.id
                          ? 'bg-primary-500 text-white border-primary-500'
                          : 'bg-background-50 text-foreground-500 border-background-200 hover:border-foreground-300'
                      }`}
                    >
                      <i className={`${f.icon} text-[9px]`}></i>
                      {f.label}
                    </button>
                  ))}
                </div>

                <div className="flex flex-wrap items-center gap-1.5">
                  {([
                    { id: 'todas' as const, label: 'Todos los Estados', icon: 'ri-list-check-3' },
                    ...Object.entries(flowStateConfig).map(([key, c]) => ({ id: key as ReportFlowState, label: c.label, icon: c.icon })),
                  ]).map((f) => (
                    <button
                      key={f.id}
                      onClick={() => setFlowFilter(f.id)}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-medium transition-all duration-200 whitespace-nowrap cursor-pointer border ${
                        flowFilter === f.id
                          ? 'bg-primary-500 text-white border-primary-500'
                          : 'bg-background-50 text-foreground-500 border-background-200 hover:border-foreground-300'
                      }`}
                    >
                      <i className={`${f.icon} text-[9px]`}></i>
                      {f.label}
                    </button>
                  ))}
                </div>

                <p className="text-[10px] text-foreground-400">
                  {filtered.length} reporte{filtered.length !== 1 ? 's' : ''} encontrado{filtered.length !== 1 ? 's' : ''}
                </p>

                <div className="space-y-2">
                  {filtered.map((r) => (
                    <ReportCard
                      key={r.id}
                      report={r}
                      isSelected={selectedReport === r.id}
                      onClick={() => setSelectedReport(selectedReport === r.id ? null : r.id)}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* VIEW: FLUJO */}
            {activeView === 'flujo' && (
              <div className="space-y-5">
                <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
                  {Object.entries(flowStateConfig).map(([key, c]) => {
                    const count = reports.filter((r) => r.flowState === key).length;
                    return (
                      <div key={key} className="p-4 rounded-2xl bg-background-50 border border-background-200/70 text-center relative overflow-hidden">
                        <div className={`absolute top-0 left-0 right-0 h-1 ${c.bgColor}`}></div>
                        <div className={`w-10 h-10 rounded-full ${c.bgColor} flex items-center justify-center mx-auto mb-2 mt-2`}>
                          <i className={`${c.icon} text-base ${c.color}`}></i>
                        </div>
                        <p className="text-lg font-extrabold text-foreground-950">{count}</p>
                        <p className="text-xs font-semibold text-foreground-700">{c.label}</p>
                        <p className="text-[9px] text-foreground-400 mt-0.5">{c.description}</p>
                      </div>
                    );
                  })}
                </div>

                <h3 className="text-sm font-extrabold text-foreground-950 flex items-center gap-2">
                  <i className="ri-hourglass-line text-amber-500"></i>
                  Reportes en Curso
                  <span className="text-[11px] text-foreground-400 font-normal">({pending.length})</span>
                </h3>

                <div className="space-y-2">
                  {pending.map((r) => (
                    <FlujoDetalleCard key={r.id} report={r} />
                  ))}
                </div>
              </div>
            )}

            {/* VIEW: REINCIDENCIAS */}
            {activeView === 'reincidencias' && (
              <div className="space-y-5">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-extrabold text-foreground-950 flex items-center gap-2">
                    <i className="ri-loop-left-line text-red-500"></i>
                    Usuarios con Reincidencia
                  </h3>
                  <div className="flex gap-2 text-[10px]">
                    <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-sky-500"></span>Vigilancia</span>
                    <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-500"></span>Reincidente</span>
                    <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-500"></span>Sancionado</span>
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-1.5">
                  {([
                    { id: 'todas' as const, label: 'Todas' },
                    { id: 'vigilancia' as const, label: 'En Vigilancia' },
                    { id: 'reincidente' as const, label: 'Reincidentes' },
                    { id: 'sancionado' as const, label: 'Sancionados' },
                  ]).map((f) => (
                    <button
                      key={f.id}
                      onClick={() => setReincFilter(f.id)}
                      className={`px-3 py-1.5 rounded-full text-[11px] font-medium transition-all duration-200 whitespace-nowrap cursor-pointer border ${
                        reincFilter === f.id
                          ? 'bg-primary-500 text-white border-primary-500'
                          : 'bg-background-50 text-foreground-500 border-background-200 hover:border-foreground-300'
                      }`}
                    >
                      {f.label}
                    </button>
                  ))}
                </div>

                <div className="space-y-3">
                  {filteredReinc.map((r) => (
                    <div key={r.id} className="p-4 rounded-xl bg-background-50 border border-background-200/70">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <img src={r.reportedTargetPhotoUrl} alt={r.reportedTargetUsername} className="w-10 h-10 rounded-full object-cover opacity-70" />
                          <div>
                            <p className="text-xs font-bold text-foreground-950">{r.reportedTargetUsername}</p>
                            <p className="text-[9px] text-foreground-500">
                              {r.totalReports} reportes totales · Último: {r.lastReportDateRelative}
                            </p>
                          </div>
                        </div>
                        <span className={`shrink-0 text-[9px] px-2.5 py-1 rounded-full font-semibold ${
                          r.status === 'sancionado' ? 'bg-red-100 text-red-700 border border-red-200' :
                          r.status === 'reincidente' ? 'bg-amber-100 text-amber-700 border border-amber-200' :
                          'bg-sky-100 text-sky-700 border border-sky-200'
                        }`}>
                          {r.statusLabel}
                        </span>
                      </div>

                      <div className="flex flex-wrap gap-1.5 mb-3">
                        {r.reportTypes.map((t) => {
                          const c = reportConfig[t];
                          return (
                            <span key={t} className={`inline-flex items-center gap-1 text-[9px] px-2 py-0.5 rounded-full font-medium ${c.bgColor} ${c.color} border border-current/20`}>
                              <i className={`${c.icon} text-[8px]`}></i>
                              {c.label}
                            </span>
                          );
                        })}
                      </div>

                      <div className="pl-[52px] space-y-1">
                        <p className="text-[9px] font-semibold text-foreground-400 uppercase tracking-wider">Historial de Sanciones</p>
                        {r.previousSanctions.map((s, si) => (
                          <p key={si} className="text-[10px] text-foreground-600 flex items-center gap-1.5">
                            <span className={`w-1.5 h-1.5 rounded-full ${
                              si === r.previousSanctions.length - 1 ? 'bg-red-500' : 'bg-foreground-300'
                            }`}></span>
                            {s}
                          </p>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* VIEW: SA DASHBOARD */}
            {activeView === 'sa-dashboard' && (
              <div className="space-y-5">
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {[
                    { label: 'Pendientes', value: saDashboardStats.reportsPending, sub: 'Sin asignar o en cola', icon: 'ri-hourglass-line', color: 'text-orange-600 bg-orange-50', borderColor: 'border-orange-200' },
                    { label: 'En Revisión', value: saDashboardStats.reportsInReview, sub: 'Con moderador asignado', icon: 'ri-search-eye-line', color: 'text-sky-600 bg-sky-50', borderColor: 'border-sky-200' },
                    { label: 'Resueltos', value: (saDashboardStats.reportsResolved / 1000).toFixed(0) + 'K', sub: 'Tasa: ' + saDashboardStats.resolutionRate, icon: 'ri-check-double-line', color: 'text-emerald-600 bg-emerald-50', borderColor: 'border-emerald-200' },
                    { label: 'Cerrados', value: (saDashboardStats.reportsClosed / 1000).toFixed(0) + 'K', sub: 'Casos archivados', icon: 'ri-archive-line', color: 'text-slate-600 bg-slate-50', borderColor: 'border-slate-200' },
                    { label: 'Reincidentes Activos', value: saDashboardStats.reincidentesActivos, sub: saDashboardStats.repeatOffenderRate + ' tasa', icon: 'ri-loop-left-line', color: 'text-red-600 bg-red-50', borderColor: 'border-red-200' },
                    { label: 'Hoy', value: saDashboardStats.reportsToday, sub: 'Esta semana: ' + (saDashboardStats.reportsThisWeek / 1000).toFixed(1) + 'K', icon: 'ri-calendar-line', color: 'text-violet-600 bg-violet-50', borderColor: 'border-violet-200' },
                  ].map((stat, i) => (
                    <div key={i} className={`p-4 rounded-2xl bg-background-50 border ${stat.borderColor} text-center`}>
                      <div className={`w-10 h-10 rounded-full ${stat.color} flex items-center justify-center mx-auto mb-2`}>
                        <i className={`${stat.icon} text-sm`}></i>
                      </div>
                      <p className="text-2xl font-extrabold text-foreground-950">{stat.value}</p>
                      <p className="text-xs font-semibold text-foreground-700">{stat.label}</p>
                      <p className="text-[9px] text-foreground-400 mt-0.5">{stat.sub}</p>
                    </div>
                  ))}
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <div className="p-4 rounded-2xl bg-background-50 border border-background-200/70">
                    <h4 className="text-xs font-bold text-foreground-400 uppercase tracking-wider mb-3 flex items-center gap-1.5">
                      <i className="ri-bar-chart-line text-amber-400"></i>
                      Distribución por Tipo
                    </h4>
                    <div className="space-y-2">
                      {Object.entries(reportConfig).map(([key, c]) => {
                        const count = reports.filter((r) => r.reportType === key).length;
                        const total = reports.length;
                        const pct = ((count / total) * 100).toFixed(1);
                        return (
                          <div key={key} className="space-y-1">
                            <div className="flex items-center justify-between text-[10px]">
                              <span className="flex items-center gap-1.5">
                                <i className={`${c.icon} text-[9px] ${c.color}`}></i>
                                {c.labelPlural}
                              </span>
                              <span className="font-semibold text-foreground-700">{count} ({pct}%)</span>
                            </div>
                            <div className="h-1.5 rounded-full bg-background-200 overflow-hidden">
                              <div className={`h-full rounded-full ${c.bgColor.replace('bg-', 'bg-').replace('50', '300')}`} style={{ width: `${pct}%` }}></div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  <div className="p-4 rounded-2xl bg-background-50 border border-background-200/70">
                    <h4 className="text-xs font-bold text-foreground-400 uppercase tracking-wider mb-3 flex items-center gap-1.5">
                      <i className="ri-scales-line text-emerald-400"></i>
                      Top Tipo Reportado
                    </h4>
                    <div className="space-y-3">
                      <div className="flex items-center gap-3 p-3 rounded-xl bg-amber-50 border border-amber-200">
                        <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                          <i className={`${reportConfig[saDashboardStats.topReportType].icon} text-base text-amber-600`}></i>
                        </div>
                        <div>
                          <p className="text-sm font-extrabold text-foreground-950">{(saDashboardStats.topReportTypeCount / 1000).toFixed(1)}K</p>
                          <p className="text-[10px] text-foreground-600">{reportConfig[saDashboardStats.topReportType].labelPlural}</p>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-center">
                        <div className="p-2.5 rounded-xl bg-background-100">
                          <p className="text-xs font-extrabold text-foreground-950">{(saDashboardStats.reportsThisMonth / 1000).toFixed(1)}K</p>
                          <p className="text-[9px] text-foreground-500">Este Mes</p>
                        </div>
                        <div className="p-2.5 rounded-xl bg-background-100">
                          <p className="text-xs font-extrabold text-foreground-950">{saDashboardStats.avgResolutionTime}</p>
                          <p className="text-[9px] text-foreground-500">Tiempo Medio</p>
                        </div>
                        <div className="p-2.5 rounded-xl bg-background-100">
                          <p className="text-xs font-extrabold text-foreground-950">{saDashboardStats.resolutionRate}</p>
                          <p className="text-[9px] text-foreground-500">Tasa Resolución</p>
                        </div>
                        <div className="p-2.5 rounded-xl bg-background-100">
                          <p className="text-xs font-extrabold text-foreground-950">{(saDashboardStats.totalReincidencias / 1000).toFixed(1)}K</p>
                          <p className="text-[9px] text-foreground-500">Reincidencias Totales</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* RULES SECTION */}
        <section className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-4">
          {reportRules.map((rule) => (
            <div key={rule.id} className={`p-5 rounded-2xl ${rule.bgColor} border border-background-200/50 text-center`}>
              <div className={`w-10 h-10 rounded-full bg-background-50 flex items-center justify-center mx-auto mb-2`}>
                <i className={`${rule.icon} text-lg ${rule.color}`}></i>
              </div>
              <p className="text-sm font-bold text-foreground-950 mb-1">{rule.title}</p>
              <p className="text-[11px] text-foreground-500">{rule.description}</p>
            </div>
          ))}
        </section>

        {/* CROSS LINKS */}
        <section className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-6 mb-6">
          {[
            { to: '/moderadores', icon: 'ri-shield-user-line', iconBg: 'bg-rose-100', iconColor: 'text-rose-600', label: 'Moderadores', desc: 'Quiénes revisan los reportes' },
            { to: '/centro-social', icon: 'ri-pulse-line', iconBg: 'bg-amber-100', iconColor: 'text-amber-600', label: 'Centro Social', desc: 'Tendencias de la comunidad' },
            { to: '/mensajes-privados', icon: 'ri-message-2-line', iconBg: 'bg-violet-100', iconColor: 'text-violet-600', label: 'Mensajes Privados', desc: 'Reportar mensajes ofensivos' },
            { to: '/centro-notificaciones', icon: 'ri-notification-3-line', iconBg: 'bg-sky-100', iconColor: 'text-sky-600', label: 'Notificaciones', desc: 'Alertas de tus reportes' },
          ].map((link, i) => (
            <Link key={i} to={link.to} className="p-4 rounded-xl bg-background-100 border border-background-200/70 hover:border-amber-300/60 transition-all duration-200 text-center">
              <div className={`w-10 h-10 rounded-full ${link.iconBg} flex items-center justify-center mx-auto mb-2`}>
                <i className={`${link.icon} text-base ${link.iconColor}`}></i>
              </div>
              <p className="text-sm font-semibold text-foreground-900">{link.label}</p>
              <p className="text-[10px] text-foreground-500 mt-1">{link.desc}</p>
            </Link>
          ))}
        </section>

        {/* REGLA FINAL */}
        <section className="p-6 rounded-2xl bg-amber-100/70 border border-amber-200/50 text-center">
          <div className="w-12 h-12 rounded-full bg-primary-500 flex items-center justify-center mx-auto mb-3">
            <i className="ri-eye-line text-xl text-white"></i>
          </div>
          <p className="text-lg font-extrabold text-foreground-950 mb-2">No eliminar automáticamente. Requiere revisión.</p>
          <p className="text-sm text-foreground-600 max-w-xl mx-auto">
            En RFW la moderación no es automática. Cada reporte — sea spam, insultos, contenido ofensivo, publicidad no autorizada o suplantación — pasa por un moderador humano que analiza el contexto, evalúa la intención y decide la acción proporcional. El sistema registra el historial completo del usuario reportado: si reincide, la sanción escala. Pero siempre, siempre, hay un criterio humano detrás de cada decisión. Porque la autorregulación comunitaria no funciona con algoritmos — funciona con personas que entienden el contexto, la cultura y los matices de una comunidad radial viva.
          </p>
        </section>
      </main>

      {/* MODAL: NUEVO REPORTE */}
      {showReportModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4 bg-black/40 backdrop-blur-sm">
          <div className="bg-background-50 rounded-2xl w-full max-w-lg border border-background-200/70 shadow-lg p-6">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-base font-extrabold text-foreground-950 flex items-center gap-2">
                <i className="ri-flag-line text-amber-500"></i>
                Nuevo Reporte
              </h3>
              <button onClick={() => setShowReportModal(false)} className="w-8 h-8 rounded-full bg-background-100 flex items-center justify-center hover:bg-background-200 transition-colors cursor-pointer">
                <i className="ri-close-line text-foreground-500"></i>
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="text-xs font-bold text-foreground-700 mb-2 block">Tipo de Reporte</label>
                <div className="grid grid-cols-1 gap-1.5">
                  {Object.entries(reportConfig).map(([key, c]) => (
                    <button
                      key={key}
                      onClick={() => setReportForm({ ...reportForm, type: key as ReportType })}
                      className={`flex items-center gap-3 p-3 rounded-xl text-left transition-all duration-200 cursor-pointer border ${
                        reportForm.type === key
                          ? 'bg-amber-50 border-amber-300'
                          : 'bg-background-100 border-background-200 hover:border-amber-200'
                      }`}
                    >
                      <div className={`w-9 h-9 rounded-full ${c.bgColor} flex items-center justify-center shrink-0`}>
                        <i className={`${c.icon} text-sm ${c.color}`}></i>
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-xs font-bold text-foreground-950">{c.label}</p>
                        <p className="text-[10px] text-foreground-500">{c.description}</p>
                      </div>
                      {reportForm.type === key && (
                        <i className="ri-check-line text-amber-600 text-sm shrink-0"></i>
                      )}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="text-xs font-bold text-foreground-700 mb-2 block">Usuario o Contenido a Reportar</label>
                <input
                  type="text"
                  value={reportForm.target}
                  onChange={(e) => setReportForm({ ...reportForm, target: e.target.value })}
                  placeholder="@usuario, enlace o descripción del contenido..."
                  className="w-full px-4 py-3 rounded-xl bg-background-100 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-amber-400 transition-colors"
                />
              </div>

              <div>
                <label className="text-xs font-bold text-foreground-700 mb-2 block">Motivo del Reporte</label>
                <textarea
                  value={reportForm.reason}
                  onChange={(e) => setReportForm({ ...reportForm, reason: e.target.value })}
                  placeholder="Describí por qué este contenido o usuario infringe las normas de la comunidad..."
                  rows={4}
                  maxLength={500}
                  className="w-full px-4 py-3 rounded-xl bg-background-100 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-amber-400 transition-colors resize-none"
                ></textarea>
                <p className="text-[9px] text-foreground-400 mt-1 text-right">{reportForm.reason.length}/500</p>
              </div>

              <div className="flex items-center gap-3 pt-2">
                <button
                  onClick={() => setShowReportModal(false)}
                  className="flex-1 px-4 py-2.5 rounded-full bg-background-100 text-foreground-600 font-semibold text-sm hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleSubmitReport}
                  disabled={!reportForm.target || !reportForm.reason}
                  className="flex-1 px-4 py-2.5 rounded-full bg-amber-500 text-white font-semibold text-sm hover:bg-amber-600 disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-200 cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-send-plane-line mr-1.5"></i>
                  Enviar Reporte
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ReportCard({ report: r, isSelected, onClick }: { report: ReportEntry; isSelected: boolean; onClick: () => void }) {
  return (
    <div
      className={`p-4 rounded-xl border transition-all duration-200 cursor-pointer ${
        isSelected
          ? 'bg-amber-50 border-amber-300/60 shadow-sm'
          : 'bg-background-50 border-background-200/70 hover:border-amber-300/60'
      }`}
      onClick={onClick}
    >
      <div className="flex items-start gap-3 mb-2">
        <div className="relative shrink-0">
          <img src={r.reportedByPhotoUrl} alt={r.reportedBy} className="w-10 h-10 rounded-full object-cover" />
          <div className={`absolute -bottom-1 -right-1 w-5 h-5 rounded-full ${reportConfig[r.reportType].bgColor} flex items-center justify-center border-2 border-background-50`}>
            <i className={`${reportConfig[r.reportType].icon} text-[8px] ${reportConfig[r.reportType].color}`}></i>
          </div>
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 mb-0.5">
            <p className="text-xs font-bold text-foreground-950">{r.reportedTargetUsername}</p>
            <TypeBadge type={r.reportType} />
            {r.isRepeatOffender && (
              <span className="inline-flex items-center gap-0.5 text-[8px] font-semibold text-red-600 bg-red-50 px-1.5 py-0.5 rounded-full">
                <i className="ri-loop-left-line text-[7px]"></i>{r.repeatCount}x
              </span>
            )}
            <span className="text-[10px] text-foreground-400 ml-auto shrink-0">{r.createdAtRelative}</span>
          </div>
          <p className="text-[10px] text-foreground-500">Reportado por {r.reportedByUsername} · {r.targetTypeLabel}</p>

          {isSelected && (
            <div className="mt-3 pt-3 border-t border-background-200/50 animate-fadeIn space-y-2">
              <p className="text-[11px] text-foreground-700">{r.reason}</p>
              <p className="text-[9px] text-foreground-400">
                <i className="ri-information-line text-[8px] mr-0.5"></i>
                {r.evidenceSummary}
              </p>

              <div className="flex flex-wrap items-center gap-2">
                <FlowBadge state={r.flowState} />
                {r.assignedReviewer && (
                  <span className="text-[10px] text-foreground-600 flex items-center gap-1">
                    <i className="ri-shield-user-line text-[9px]"></i>
                    {r.assignedReviewer}
                  </span>
                )}
                {r.resolution && (
                  <ResolutionBadge resolution={r.resolution} />
                )}
              </div>

              {r.resolutionDetail && (
                <div className="p-2.5 rounded-lg bg-background-100">
                  <p className="text-[10px] text-foreground-600">
                    <i className="ri-check-double-line text-[9px] text-emerald-500 mr-1"></i>
                    {r.resolutionDetail}
                  </p>
                </div>
              )}

              <p className="text-[9px] text-foreground-400">{r.context}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function FlujoDetalleCard({ report: r }: { report: ReportEntry }) {
  return (
    <div className="p-4 rounded-xl bg-background-50 border border-background-200/70">
      <div className="flex items-start gap-3 mb-2">
        <div className="relative shrink-0">
          <img src={r.reportedByPhotoUrl} alt={r.reportedBy} className="w-10 h-10 rounded-full object-cover" />
          <div className={`absolute -bottom-1 -right-1 w-5 h-5 rounded-full ${reportConfig[r.reportType].bgColor} flex items-center justify-center border-2 border-background-50`}>
            <i className={`${reportConfig[r.reportType].icon} text-[8px] ${reportConfig[r.reportType].color}`}></i>
          </div>
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 mb-0.5">
            <TypeBadge type={r.reportType} />
            <FlowBadge state={r.flowState} />
            <span className="text-[10px] text-foreground-400 ml-auto shrink-0">{r.createdAtRelative}</span>
          </div>
          <p className="text-xs font-bold text-foreground-950">{r.reportedTargetUsername}</p>
          <p className="text-[10px] text-foreground-600 mb-1">{r.reason}</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-[10px]">
            <div>
              <span className="text-foreground-400">Reportado por: </span>
              <span className="font-medium text-foreground-700">{r.reportedByUsername}</span>
            </div>
            <div>
              <span className="text-foreground-400">Tipo: </span>
              <span className="font-medium text-foreground-700">{r.targetTypeLabel}</span>
            </div>
          </div>
          {r.assignedReviewer && (
            <div className="flex items-center gap-1.5 mt-2">
              <img src={r.assignedReviewerPhotoUrl} alt={r.assignedReviewer} className="w-5 h-5 rounded-full object-cover" />
              <p className="text-[10px] text-foreground-600">
                <span className="text-foreground-400">Asignado a </span>
                <span className="font-semibold text-foreground-700">{r.assignedReviewer}</span>
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}